import { useEffect } from 'react';

export function useAdvancedGsap() {
  useEffect(() => {
    let gsapInstance;
    let ScrollTriggerInstance;
    
    const initAnimations = async () => {
      try {
        const gsapModule = 'gsap';
        const stModule = 'gsap/ScrollTrigger';
        
        const { gsap } = await import(/* @vite-ignore */ gsapModule);
        const { ScrollTrigger } = await import(/* @vite-ignore */ stModule);
        
        gsapInstance = gsap;
        ScrollTriggerInstance = ScrollTrigger;
        gsap.registerPlugin(ScrollTrigger);

        // Text split animations
        gsap.utils.toArray('.split-text').forEach((text) => {
          const chars = text.textContent.split('').map(char => 
            `<span class="char">${char === ' ' ? '&nbsp;' : char}</span>`
          ).join('');
          text.innerHTML = chars;
          
          gsap.from(text.querySelectorAll('.char'), {
            opacity: 0,
            y: 100,
            rotateX: -90,
            stagger: 0.02,
            scrollTrigger: {
              trigger: text,
              start: 'top 80%',
              end: 'top 50%',
              scrub: 1
            }
          });
        });

        // Parallax depth layers
        gsap.utils.toArray('.parallax-layer').forEach((layer) => {
          const depth = layer.dataset.depth || 1;
          gsap.to(layer, {
            y: () => -100 * depth,
            ease: 'none',
            scrollTrigger: {
              trigger: layer,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true
            }
          });
        });

        // 3D card tilts
        gsap.utils.toArray('.card-3d').forEach((card) => {
          gsap.set(card, { transformPerspective: 1000 });
          
          gsap.from(card, {
            rotateY: -30,
            rotateX: 20,
            scale: 0.8,
            opacity: 0,
            scrollTrigger: {
              trigger: card,
              start: 'top 80%',
              end: 'top 50%',
              scrub: 1
            }
          });
        });

        // Skew on scroll
        let proxy = { skew: 0 };
        let skewSetter = gsap.quickSetter('.skew-section', 'skewY', 'deg');
        let clamp = gsap.utils.clamp(-3, 3);
        
        ScrollTrigger.create({
          onUpdate: (self) => {
            let skew = clamp(self.getVelocity() / -300);
            if (Math.abs(skew) > Math.abs(proxy.skew)) {
              proxy.skew = skew;
              gsap.to(proxy, {
                skew: 0,
                duration: 0.8,
                ease: 'power3',
                overwrite: true,
                onUpdate: () => skewSetter(proxy.skew)
              });
            }
          }
        });

        // Magnetic buttons
        document.querySelectorAll('.magnetic-btn').forEach(btn => {
          btn.addEventListener('mousemove', (e) => {
            const rect = btn.getBoundingClientRect();
            const x = e.clientX - rect.left - rect.width / 2;
            const y = e.clientY - rect.top - rect.height / 2;
            
            gsap.to(btn, {
              x: x * 0.3,
              y: y * 0.3,
              duration: 0.3,
              ease: 'power2.out'
            });
          });
          
          btn.addEventListener('mouseleave', () => {
            gsap.to(btn, {
              x: 0,
              y: 0,
              duration: 0.3,
              ease: 'power2.out'
            });
          });
        });

        // Horizontal scroll sections
        gsap.utils.toArray('.horizontal-scroll').forEach((section) => {
          const wrapper = section.querySelector('.horizontal-wrapper');
          const items = section.querySelectorAll('.horizontal-item');
          
          gsap.to(items, {
            xPercent: -100 * (items.length - 1),
            ease: 'none',
            scrollTrigger: {
              trigger: section,
              pin: true,
              scrub: 1,
              snap: 1 / (items.length - 1),
              end: () => '+=' + section.offsetWidth
            }
          });
        });

        // Reveal with rotation
        gsap.utils.toArray('.reveal-rotate').forEach((elem) => {
          gsap.from(elem, {
            rotateZ: 5,
            y: 100,
            opacity: 0,
            scrollTrigger: {
              trigger: elem,
              start: 'top 85%',
              end: 'top 60%',
              scrub: 1
            }
          });
        });

        // Sticky elements with parallax
        gsap.utils.toArray('.sticky-parallax').forEach((elem) => {
          ScrollTrigger.create({
            trigger: elem,
            start: 'top top',
            end: 'bottom top',
            pin: true,
            pinSpacing: false
          });
        });

      } catch (error) {
        console.log('GSAP not installed yet');
      }
    };

    initAnimations();

    return () => {
      if (ScrollTriggerInstance) {
        ScrollTriggerInstance.getAll().forEach(trigger => trigger.kill());
      }
    };
  }, []);
}
